import React, { useState, useRef, useEffect } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import { Upload, FileText, X, Video, Eye } from "lucide-react";
import { AppButton, AppBadge } from "../ui";
import apiClient from "../../utils/apiClient";
import axios from "axios";
import "./Materials.scss";

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1073741824).toFixed(2)} GB`;
};

const ACCEPTED_TYPES = ".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar,.jpg,.jpeg,.png";
const ACCEPTED_TYPES_WITH_VIDEO = ".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar,.jpg,.jpeg,.png,.mp4,.mov,.webm,.mkv";
const VIDEO_EXTS = ["mp4", "mov", "webm", "mkv"];

/**
 * Props:
 *   show, onClose, onSuccess
 *   batchId       – required for batch/video-level uploads
 *   courseId       – required for course-level uploads (admin only)
 *   videoId        – if provided, locks level to "video" for that video
 *   defaultLevel   – "video" | "batch" | "course" (default "batch")
 *   hideLevel      – if true, hide the level selector (used when context is obvious)
 */
const UploadMaterialModal = ({
  show, onClose, batchId, courseId, videoId,
  defaultLevel = "batch", hideLevel = false, onSuccess,
}) => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [topic, setTopic] = useState("");
  const [sessionDate, setSessionDate] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("other");
  const [level, setLevel] = useState(defaultLevel);
  const [selectedVideoId, setSelectedVideoId] = useState(videoId || "");
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const fileInputRef = useRef(null);

  // If videoId prop is given, lock level to "video"
  useEffect(() => {
    if (videoId) {
      setLevel("video");
      setSelectedVideoId(videoId);
    } else {
      setLevel(defaultLevel);
    }
  }, [videoId, defaultLevel]);

  // Fetch batch videos when level is "video" and batchId is available
  useEffect(() => {
    if (show && level === "video" && batchId && !videoId) {
      apiClient.get(`/videos/batch/${batchId}`, { params: { page: 1, limit: 200 } })
        .then(({ data }) => {
          if (data.success) setVideos(data.data || []);
        })
        .catch(() => setVideos([]));
    }
  }, [show, level, batchId, videoId]);

  const resetForm = () => {
    setFile(null);
    setTitle("");
    setTopic("");
    setSessionDate("");
    setDescription("");
    setCategory("other");
    setLevel(videoId ? "video" : defaultLevel);
    setSelectedVideoId(videoId || "");
    setUploading(false);
    setUploadProgress(0);
    setError("");
    setDragActive(false);
    setIsPreview(false);
  };

  const handleClose = () => {
    if (uploading) return;
    resetForm();
    onClose();
  };

  const handleFileChange = (selectedFile) => {
    if (!selectedFile) return;
    setError("");
    setFile(selectedFile);

    // Auto-fill title from filename if empty
    if (!title) {
      const name = selectedFile.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, " ");
      setTitle(name);
    }

    // Auto-detect category from extension
    const ext = selectedFile.name.split(".").pop().toLowerCase();
    if (["ppt", "pptx"].includes(ext)) setCategory("ppt");
    else if (["doc", "docx", "txt"].includes(ext)) setCategory("script");
    else if (["pdf"].includes(ext)) setCategory("notes");
    else if (VIDEO_EXTS.includes(ext)) setCategory("other");
  };

  const isVideoFile = file && VIDEO_EXTS.includes(file.name.split(".").pop().toLowerCase());
  const isCourseLevel = level === "course";
  const acceptTypes = isCourseLevel ? ACCEPTED_TYPES_WITH_VIDEO : ACCEPTED_TYPES;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFileChange(e.dataTransfer.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate based on level
    if (!file || !title.trim()) {
      setError("File and title are required.");
      return;
    }
    if (level === "batch" && !sessionDate) {
      setError("Session date is required for batch-level materials.");
      return;
    }
    if (level === "video" && !selectedVideoId) {
      setError("Please select a video for video-level materials.");
      return;
    }

    setUploading(true);
    setError("");
    setUploadProgress(0);

    try {
      // Upload file via server-side upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('level', level);
      formData.append('title', title.trim());
      formData.append('description', description.trim());
      formData.append('topic', topic.trim());
      formData.append('category', category);
      formData.append('fileName', file.name);
      formData.append('fileSize', file.size);

      if (level === "video") {
        formData.append('videoId', selectedVideoId);
        formData.append('batchId', batchId);
      } else if (level === "batch") {
        formData.append('batchId', batchId);
        formData.append('sessionDate', sessionDate);
      } else if (level === "course") {
        formData.append('courseId', courseId);
        if (isVideoFile) {
          formData.append('isPreview', isPreview);
        }
      }

      if (sessionDate && level !== "batch") {
        formData.append('sessionDate', sessionDate);
      }

      const { data: uploadData } = await apiClient.post("/materials/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      });

      if (!uploadData.success) {
        setError(uploadData.message || "Failed to upload.");
        setUploading(false);
        return;
      }

      resetForm();
      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.response?.data?.message || "Upload failed. Please try again.");
    }

    setUploading(false);
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="upload-material-modal">
      <Modal.Header closeButton={!uploading}>
        <Modal.Title className="fs-6 fw-semibold">
          {level === "video" ? "Upload Video Material" : level === "course" ? "Upload Course Material" : "Upload Class Material"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Level Selector */}
          {!hideLevel && (
            <Form.Group className="mb-3">
              <Form.Label className="small fw-medium">Material Level</Form.Label>
              <Form.Select
                value={level}
                onChange={(e) => {
                  setLevel(e.target.value);
                  setSelectedVideoId("");
                  setError("");
                }}
                disabled={uploading || !!videoId}
                size="sm"
              >
                <option value="batch">Batch Level — for this batch only</option>
                <option value="video">Video Level — attached to a recording</option>
                {courseId && <option value="course">Course Level — shared across all batches</option>}
              </Form.Select>
            </Form.Group>
          )}

          {/* Video picker — only when level=video and no fixed videoId */}
          {level === "video" && !videoId && (
            <Form.Group className="mb-3">
              <Form.Label className="small fw-medium">Attach to Video *</Form.Label>
              <Form.Select
                value={selectedVideoId}
                onChange={(e) => setSelectedVideoId(e.target.value)}
                disabled={uploading}
                size="sm"
              >
                <option value="">Select a video…</option>
                {videos.map((v) => (
                  <option key={v._id} value={v._id}>
                    {v.title}{v.recordedAt ? ` (${new Date(v.recordedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })})` : ""}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          {/* Drop Zone */}
          {!file ? (
            <div
              className={`material-dropzone ${dragActive ? "material-dropzone--active" : ""}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={24} />
              <p className="mb-1 fw-medium">Drop file here or click to browse</p>
              <small className="text-muted">{isCourseLevel ? 'PDF, PPT, DOC, XLS, TXT, ZIP, images, videos (MP4, MOV, WebM)' : 'PDF, PPT, DOC, XLS, TXT, ZIP, images'}</small>
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptTypes}
                onChange={(e) => handleFileChange(e.target.files?.[0])}
                hidden
              />
            </div>
          ) : (
            <div className="material-dropzone material-dropzone--selected">
              {isVideoFile ? <Video size={20} /> : <FileText size={20} />}
              <div className="flex-grow-1">
                <p className="mb-0 small fw-medium text-truncate">{file.name}</p>
                <small className="text-muted">{formatFileSize(file.size)}{isVideoFile && ' • Video'}</small>
              </div>
              {!uploading && (
                <button type="button" className="btn btn-sm" onClick={() => setFile(null)}>
                  <X size={16} />
                </button>
              )}
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="mt-2">
              <div className="progress" style={{ height: 6 }}>
                <div
                  className="progress-bar bg-primary"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <small className="text-muted">{uploadProgress}% uploaded</small>
            </div>
          )}

          {/* Title */}
          <Form.Group className="mt-3">
            <Form.Label className="small fw-medium">Title *</Form.Label>
            <Form.Control
              type="text"
              placeholder={isVideoFile ? 'e.g., Course Introduction' : 'e.g., Day 5 - React Hooks PPT'}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={uploading}
              size="sm"
            />
          </Form.Group>

          {/* Free Preview Toggle — only for course-level videos */}
          {isCourseLevel && isVideoFile && (
            <div className="material-preview-toggle mt-3">
              <Form.Check
                type="switch"
                id="free-preview-toggle"
                label={
                  <span className="small fw-medium">
                    <Eye size={14} className="me-1" />
                    Free Preview
                  </span>
                }
                checked={isPreview}
                onChange={(e) => setIsPreview(e.target.checked)}
                disabled={uploading}
              />
              <small className="text-muted d-block mt-1">
                Allow unenrolled students to watch this video before purchasing
              </small>
            </div>
          )}

          {/* Category */}
          <Form.Group className="mt-3">
            <Form.Label className="small fw-medium">Category</Form.Label>
            <Form.Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={uploading}
              size="sm"
            >
              <option value="ppt">PPT / Presentation</option>
              <option value="script">Script</option>
              <option value="notes">Notes</option>
              <option value="other">Other</option>
            </Form.Select>
          </Form.Group>

          {/* Session Date — required only for batch level */}
          {level === "batch" && (
            <Form.Group className="mt-3">
              <Form.Label className="small fw-medium">Session Date *</Form.Label>
              <Form.Control
                type="date"
                value={sessionDate}
                onChange={(e) => setSessionDate(e.target.value)}
                disabled={uploading}
                size="sm"
              />
            </Form.Group>
          )}

          {/* Topic */}
          <Form.Group className="mt-3">
            <Form.Label className="small fw-medium">Topic</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., React Hooks"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              disabled={uploading}
              size="sm"
            />
          </Form.Group>

          {/* Description */}
          <Form.Group className="mt-3">
            <Form.Label className="small fw-medium">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Optional description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={uploading}
              size="sm"
            />
          </Form.Group>

          {error && <div className="text-danger small mt-2">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <AppButton variant="outline" size="sm" onClick={handleClose} disabled={uploading}>
            Cancel
          </AppButton>
          <AppButton
            variant="default"
            size="sm"
            type="submit"
            disabled={
              uploading || !file || !title.trim() ||
              (level === "batch" && !sessionDate) ||
              (level === "video" && !selectedVideoId)
            }
          >
            {uploading ? (
              <>
                <Spinner animation="border" size="sm" /> Uploading…
              </>
            ) : (
              <>
                <Upload size={14} /> Upload
              </>
            )}
          </AppButton>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UploadMaterialModal;
